import { ICurrencyFormat } from "../containers/Money/Money.actions";

const validateAmount = (amount: number): any => {
  if (amount === null) {
    return false;
  } else if (!Number.isInteger(amount)) {
    throw new Error(
      "Could not validate amount because value was not an integer"
    );
  } else if (amount < 0) {
    throw new Error("Could not validate amount because value was negative");
  }
  return true;
};

const formatAmount = (
  integerFormatted: string,
  decimalDivider: string = "",
  fraction: string = ""
): string => {
  const formattedAmount = `${integerFormatted}${decimalDivider}${fraction}`;
  return `${formattedAmount}`;
};

export const formatCashLabel = (
  label: string,
  currencyFormat: ICurrencyFormat
) => {
  if (!currencyFormat.showCurrencyChar) {
    return label;
  }

  const currencyCode = currencyFormat.symbol || currencyFormat.iso;

  return `${label} (${currencyCode})`;
};

export const formatCashAmount = (
  amount: number,
  currencyFormat: ICurrencyFormat
) => {
  const formattedAmount = formatCurrency(amount, currencyFormat);
  if (!currencyFormat.showCurrencyChar) {
    return formattedAmount;
  }

  const currencyCode = currencyFormat.symbol || currencyFormat.iso;

  return `${currencyCode} ${formattedAmount}`;
};

export const formatCoinsLabel = (label: string, coinsLabel?: string) => {
  return `${label}${coinsLabel ? ` (${coinsLabel})` : ""}`;
};

const formatInteger = (
  integerString: string,
  thousandsDivider: string
): string => {
  const THOUSAND_DIGITS = 3;

  const formattedInteger = integerString
    .split("")
    .reverse()
    .map((d, i) =>
      i > 0 && i % THOUSAND_DIGITS === 0 ? d + thousandsDivider : d
    )
    .reverse()
    .join("");

  return formattedInteger;
};

const formatCurrency = (value: number, configuration: ICurrencyFormat) => {
  const isValid = validateAmount(value);
  if (!isValid) {
    return "";
  }
  const { decimals, decimalDivider, thousandsDivider } = configuration;

  const [integerString, fraction] = Math.abs(value / 100)
    .toFixed(decimals)
    .split(".");

  const formattedInteger = formatInteger(integerString, thousandsDivider);
  const formattedAmount = formatAmount(
    formattedInteger,
    decimalDivider,
    fraction
  );

  return formattedAmount;
};

export const formatCash = (
  cents: number,
  configuration: ICurrencyFormat
): string => formatCurrency(cents, configuration);

export const formatCoins = (value: number, configuration: ICurrencyFormat) => {
  const isValid = validateAmount(value);
  if (!isValid) {
    return "";
  }

  const { coinsDivider } = configuration;
  const integerString = value.toString();
  const formattedInteger = formatInteger(integerString, coinsDivider);
  const formattedAmount = formatAmount(formattedInteger);
  return formattedAmount;
};

export const formatCoinValue = (
  coinValue: number,
  configuration: ICurrencyFormat
): string => formatCurrency(coinValue, { ...configuration, decimals: 2 });
